import { memo, useCallback, useMemo, useRef, useState } from 'react'
import type { TranscriptSegment, TranscriptVersion, TranscriptWord } from '../api/types'
import { languageName } from '../utils/format'
import { Button, ErrorNote } from './ui'

const SPEAKER_TONES = 6

/** `m:ss`, or `h:mm:ss` from an hour on. */
function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = String(total % 60).padStart(2, '0')
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`
}

/** Index of the last item that started at or before `ms`, or -1. Items are in time order. */
function lastStarted(items: { start_ms: number }[], ms: number): number {
  let low = 0
  let high = items.length - 1
  let found = -1
  while (low <= high) {
    const middle = (low + high) >> 1
    if ((items[middle]?.start_ms ?? Infinity) <= ms) {
      found = middle
      low = middle + 1
    } else {
      high = middle - 1
    }
  }
  return found
}

export interface TranscriptViewProps {
  version: TranscriptVersion
  /** The player's position, for highlighting the word being spoken. */
  currentMs: number
  onSeek: (ms: number) => void
  canEdit: boolean
  captions: { vtt: string; srt: string }
  onEditSegment: (segmentId: string, text: string) => Promise<void>
  onRenameSpeaker: (from: string, to: string) => Promise<void>
  saving: boolean
  error: unknown
  /** Shift-click two words to pick a range, then make it a clip. */
  onMakeClip?: (startMs: number, endMs: number) => Promise<unknown>
}

interface Range {
  start: number
  end: number
}

/** A transcript beside the player: click any word to hear it, edit a segment, rename a speaker. */
export function TranscriptView({ version, currentMs, onSeek, canEdit, captions, onEditSegment, onRenameSpeaker, saving, error, onMakeClip }: TranscriptViewProps) {
  const { transcript, speakers } = version
  const tones = useMemo(() => new Map(speakers.map((name, i) => [name, i % SPEAKER_TONES])), [speakers])
  const [editing, setEditing] = useState<string | null>(null)
  const [selection, setSelection] = useState<Range | null>(null)
  const [making, setMaking] = useState(false)
  const anchor = useRef<TranscriptWord | null>(null)
  const pick = useCallback(
    (word: TranscriptWord, extend: boolean) => {
      if (!extend || !onMakeClip) {
        onSeek(word.start_ms)
        return
      }
      const first = anchor.current
      if (!first) {
        anchor.current = word
        setSelection({ start: word.start_ms, end: word.end_ms })
        return
      }
      setSelection({ start: Math.min(first.start_ms, word.start_ms), end: Math.max(first.end_ms, word.end_ms) })
    },
    [onSeek, onMakeClip],
  )
  const clearSelection = () => {
    anchor.current = null
    setSelection(null)
  }
  const makeClip = () => {
    if (!selection || !onMakeClip) return
    setMaking(true)
    onMakeClip(selection.start, selection.end).then(clearSelection, () => undefined).finally(() => setMaking(false))
  }
  const save = useCallback(
    (segmentId: string, text: string) => {
      // A failure stays on screen through `error`; the editor stays open.
      onEditSegment(segmentId, text).then(() => setEditing(null), () => undefined)
    },
    [onEditSegment],
  )
  const active = lastStarted(transcript.segments, currentMs)
  return (
    <div className="transcript">
      <div className="transcript-bar">
        <div className="row">
          {speakers.map((name) => (
            <SpeakerChip key={name} name={name} tone={tones.get(name) ?? 0} canEdit={canEdit} busy={saving} onRename={(to) => onRenameSpeaker(name, to)} />
          ))}
        </div>
        <div className="row small">
          <span className="muted">
            {languageName(version.language)}
            {version.language ? ' · ' : ''}Version {version.version}
            {version.note ? ` · ${version.note}` : ''}
          </span>
          <a href={captions.vtt} download>Download VTT</a>
          <a href={captions.srt} download>Download SRT</a>
        </div>
      </div>
      {selection ? (
        <div className="notice row between tx-selection" data-testid="transcript-selection">
          <span>
            Selected {clock(selection.start)}–{clock(selection.end)} ({Math.round((selection.end - selection.start) / 1000)} s)
          </span>
          <span className="row">
            <Button busy={making} onClick={makeClip}>
              Make a clip
            </Button>
            <Button variant="ghost" onClick={clearSelection}>
              Clear
            </Button>
          </span>
        </div>
      ) : (
        onMakeClip && <span className="muted small">Shift-click two words to pick a clip.</span>
      )}
      {error != null && <ErrorNote error={error} />}
      <div className="transcript-body" data-testid="transcript">
        {transcript.segments.length === 0 && <p className="muted">No speech was found in this recording.</p>}
        {transcript.segments.map((segment, index) => (
          <SegmentRow
            key={segment.id}
            segment={segment}
            tone={segment.speaker ? (tones.get(segment.speaker) ?? 0) : null}
            activeMs={index === active ? currentMs : null}
            onSeek={onSeek}
            editing={editing === segment.id}
            canEdit={canEdit}
            saving={saving}
            onEditing={setEditing}
            onSave={save}
            selection={selection && selection.start < segment.end_ms && selection.end > segment.start_ms ? selection : null}
            onWord={pick}
          />
        ))}
      </div>
    </div>
  )
}

/** Only the segment being played gets `activeMs`, so only it re-renders as time passes. */
const SegmentRow = memo(function SegmentRow({
  segment,
  tone,
  activeMs,
  onSeek,
  editing,
  canEdit,
  saving,
  onEditing,
  onSave,
  selection,
  onWord,
}: {
  segment: TranscriptSegment
  tone: number | null
  activeMs: number | null
  onSeek: (ms: number) => void
  editing: boolean
  canEdit: boolean
  saving: boolean
  onEditing: (segmentId: string | null) => void
  onSave: (segmentId: string, text: string) => void
  selection: Range | null
  onWord: (word: TranscriptWord, extend: boolean) => void
}) {
  const current = activeMs == null ? -1 : lastStarted(segment.words, activeMs)
  return (
    <div className={`transcript-segment${activeMs != null ? ' is-active' : ''}`} data-segment={segment.id}>
      <div className="transcript-meta">
        {segment.speaker && <span className={`tx-speaker tx-tone-${tone ?? 0}`}>{segment.speaker}</span>}
        <button type="button" className="tx-link muted small" onClick={() => onSeek(segment.start_ms)} title="Play from here">
          {clock(segment.start_ms)}
        </button>
        {canEdit && !editing && (
          <button type="button" className="tx-link small" onClick={() => onEditing(segment.id)}>
            Edit
          </button>
        )}
      </div>
      {editing ? (
        <SegmentEditor text={segment.text} saving={saving} onCancel={() => onEditing(null)} onSave={(text) => onSave(segment.id, text)} />
      ) : (
        <p className="transcript-text">
          {segment.words.length === 0
            ? segment.text
            : segment.words.map((word, i) =>
                word.kind === 'silence' ? (
                  <span
                    key={i}
                    className="tx-pause"
                    title={`Pause, ${((word.end_ms - word.start_ms) / 1000).toFixed(1)} s`}
                    onClick={() => onSeek(word.start_ms)}
                  >
                    {'… '}
                  </span>
                ) : (
                  <span
                    key={i}
                    className={`tx-word${word.kind === 'filler' ? ' tx-filler' : ''}${i === current ? ' is-current' : ''}${
                      selection && word.start_ms >= selection.start && word.end_ms <= selection.end ? ' is-selected' : ''
                    }`}
                    onClick={(event) => onWord(word, event.shiftKey)}
                  >
                    {word.text}{' '}
                  </span>
                ),
              )}
        </p>
      )}
    </div>
  )
})

function SegmentEditor({ text, saving, onSave, onCancel }: { text: string; saving: boolean; onSave: (text: string) => void; onCancel: () => void }) {
  const [value, setValue] = useState(text)
  return (
    <form
      className="tx-editor stack-sm"
      onSubmit={(event) => {
        event.preventDefault()
        onSave(value)
      }}
    >
      <textarea
        rows={Math.min(8, Math.max(2, Math.ceil(text.length / 80)))}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onCancel()
        }}
        maxLength={5000}
        autoFocus
        aria-label="Segment text"
      />
      <div className="row">
        <Button type="submit" busy={saving}>Save</Button>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <span className="muted small">Words you keep keep their timestamps.</span>
      </div>
    </form>
  )
}

function SpeakerChip({ name, tone, canEdit, busy, onRename }: { name: string; tone: number; canEdit: boolean; busy: boolean; onRename: (to: string) => Promise<void> }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(name)
  if (!canEdit) return <span className={`tx-chip tx-tone-${tone}`}>{name}</span>
  if (!editing) {
    return (
      <button
        type="button"
        className={`tx-chip tx-tone-${tone}`}
        title="Rename this speaker"
        onClick={() => {
          setValue(name)
          setEditing(true)
        }}
      >
        {name}
      </button>
    )
  }
  return (
    <form
      className="row"
      onSubmit={(event) => {
        event.preventDefault()
        const to = value.trim()
        if (!to || to === name) {
          setEditing(false)
          return
        }
        onRename(to).then(() => setEditing(false), () => undefined)
      }}
    >
      <input className="tx-chip-input" value={value} onChange={(event) => setValue(event.target.value)} maxLength={60} autoFocus aria-label={`New name for ${name}`} />
      <Button type="submit" busy={busy}>Rename</Button>
      <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
    </form>
  )
}
